import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useSession } from '../../contexts/SessionContext';

interface UserProfileData {
  full_name: string;
  email: string;
}

export function UserProfile() {
  const navigate = useNavigate();
  const { user, signOut } = useSession();
  const [formData, setFormData] = useState<UserProfileData>({
    full_name: '',
    email: user?.email || '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      if (!user) return;

      const { data, error: queryError } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .single();

      if (queryError) throw queryError;

      if (data) {
        setFormData({
          full_name: data.full_name || '',
          email: data.email || user.email || '',
        });
      }
    } catch (err) {
      console.error('Error loading user profile:', err);
      setError('Failed to load user profile');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      if (!user) throw new Error('No user found');

      // Validate full name
      const trimmedName = formData.full_name.trim();
      if (!trimmedName) {
        throw new Error('Full name is required');
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: trimmedName,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setSuccessMessage('Profile updated successfully');
      
      // Update form data with trimmed name
      setFormData(prev => ({ ...prev, full_name: trimmedName }));
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to sign out');
    }
  };

  return (
    <div className="max-w-2xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="p-4 bg-green-50">
            <p className="text-sm text-green-600">{successMessage}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Full Name
          </label>
          <input
            type="text"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300"
            placeholder="Enter your full name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            disabled
            className="mt-1 block w-full rounded-md border-gray-300"
          />
          <p className="mt-1 text-sm text-gray-500">
            Email cannot be changed
          </p>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Sign out of your account on this device.
        </p>
        <button
          type="button"
          onClick={handleSignOut}
          className="inline-flex justify-center py-2 px-4 border-2 border-[#8B5CF6] bg-white text-[#8B5CF6] text-sm font-medium rounded-md hover:bg-[#8B5CF6] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8B5CF6] transition-colors"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}